import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { useChatStore } from '@/stores/chat'
import { useToastStore } from '@/stores/toast'

/** True for the routes whose whole screen is the conversation: `/` and `/chat/:id`. */
export function isChatRouteName(name: unknown): boolean {
  return name === 'home' || name === 'chat'
}

/** Binds the address to the open conversation: `/` is always a new chat, `/chat/:id` is that one. */
export function useChatRoute() {
  const route = useRoute()
  const router = useRouter()
  const chat = useChatStore()
  const toast = useToastStore()
  const { t } = useI18n()

  const turnInFlight = computed(
    () => chat.sendStatus === 'submitting' || chat.sendStatus === 'streaming',
  )

  let deferredNewChat = false
  // Held across the turn that creates a thread, until the address claim in `withThreadAddress` has run.
  let claimingAddress = false

  function newChat(): void {
    if (turnInFlight.value) {
      deferredNewChat = true
      return
    }
    deferredNewChat = false
    chat.startNewThread('chat')
  }

  watch(
    () => [route.name, route.params.id] as const,
    ([name, id]) => {
      deferredNewChat = false
      if (name === 'chat' && typeof id === 'string') {
        chat.openThread(id)
        return
      }
      if (name === 'home') newChat()
    },
    { immediate: true },
  )

  watch(turnInFlight, (inFlight) => {
    if (inFlight || !deferredNewChat || claimingAddress) return
    if (route.name === 'home') newChat()
    else deferredNewChat = false
  })

  watch(
    () => chat.messages,
    (state) => {
      if (state.status !== 'error') return
      toast.error(t('chat.errors.loadThreadFailed'), state.message)
      if (route.name === 'chat') void router.replace({ name: 'home' })
    },
  )

  /** Runs a turn and, when that turn is the one that created the thread, moves the address onto it. */
  async function withThreadAddress(turn: () => Promise<void>): Promise<void> {
    claimingAddress = chat.activeThreadId === null
    try {
      await turn()
    } finally {
      if (claimingAddress) {
        claimingAddress = false
        deferredNewChat = false
        const id = chat.activeThreadId
        if (id !== null && route.name === 'home') {
          await router.replace({ name: 'chat', params: { id } })
        }
      }
    }
  }

  return { turnInFlight, newChat, withThreadAddress }
}
